<template>
  <component :is="tag" :class="textClasses">
    <slot />
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue'

export type TextSize = 'xs' | 'sm' | 'base' | 'lg'
export type TextWeight = 'normal' | 'medium' | 'semibold' | 'bold'
export type TextColor = 'default' | 'secondary' | 'primary' | 'error' | 'success' | 'muted'

export interface TextProps {
  /** Tamanho do texto */
  size?: TextSize
  /** Peso da fonte */
  weight?: TextWeight
  /** Cor do texto */
  color?: TextColor
  /** Tag HTML a ser usada */
  as?: 'p' | 'span' | 'div'
  /** Se deve truncar com ellipsis */
  truncate?: boolean
  /** Número máximo de linhas (line-clamp) */
  lines?: number
}

const props = withDefaults(defineProps<TextProps>(), {
  size: 'base',
  weight: 'normal',
  color: 'default',
  as: 'p',
  truncate: false,
})

const tag = computed(() => props.as)

const sizeClasses: Record<TextSize, string> = {
  xs: 'text-xs',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
}

const weightClasses: Record<TextWeight, string> = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
}

const colorClasses: Record<TextColor, string> = {
  default: 'text-default-black',
  secondary: 'text-subtitle-gray',
  primary: 'text-primary',
  error: 'text-error',
  success: 'text-success',
  muted: 'text-input-outline-gray',
}

const textClasses = computed(() => {
  const classes = [
    'font-be-vietnam leading-relaxed',
    sizeClasses[props.size],
    weightClasses[props.weight],
    colorClasses[props.color],
  ]

  if (props.truncate) {
    classes.push('truncate')
  }

  if (props.lines) {
    classes.push(`line-clamp-${props.lines}`)
  }

  return classes
})
</script>

